<template>

    <div :class="showPreview ? 'xl:flex' : ''">

        <div
            v-if="showPreview"
            class="relative overflow-hidden lg:w-2/5"
        >
            <div
                v-lazy:background-image="backgroundImageUrl"
                class="image-wrapper__bg"
            />
            <ProductCustomisationCanvas
                v-if="image && textVariation && !isVariationImage && (customerImageUrl || text || textLine2)"
                class="relative"
                @change="updateCanvasPreview"
            />
            <img
                v-else
                :src="image"
                alt=""
                class="relative w-full"
            >
        </div>

        <div
            :class="showPreview ? 'flex-grow px-10 py-8 lg:w-3/5' : ''"
            class="relative"
        >
            <div
                v-if="showPreview"
                class="flex justify-between"
            >
                <div>
                    <div class="text-xl mb-1 font-semibold tracking-wide">
                        Customisation
                    </div>
                    <div class="leading-tight">
                        {{ product.name }}
                    </div>
                </div>
                <CloseIcon
                    class="close-icon mt-1"
                    @click="closeModal"
                />
            </div>

            <div class="mt-8">

                <ProductCustomisationVariationInput
                    v-for="(type, index) in product.variation_types"
                    :key="index"
                    :variation-type="type"
                    class="mb-5"
                    @selected="updateSelectedVariations($event)"
                    @imageUpdated="imageUpdated"
                    @showStockNotification="displayStockNotification"
                />

            </div>

            <div class="mt-8">

                <div class="flex items-center justify-between gap-4 md:justify-normal gap-4 fixed md:relative bottom-0 left-0 right-0 bg-white px-4 py-2 md:p-0 z-10 border-t md:border-t-0 pt-2 border-green-lightest">
                    <Button
                        v-show="!quantityInBasket"
                        :disabled="!canBuy"
                        :loading="isBuyButtonLoading"
                        text="Buy"
                        @click="handleBuyButtonClick"
                    >
                        <template #icon>
                            <BasketIcon class="h-5 mr-2" />
                        </template>
                    </Button>
                    <ProductQuantityInput
                        v-if="quantityInBasket"
                        :product-id="product.id"
                        :product-variations-compound="productVariationsCompound"
                        :product-text-variation="text"
                        :quantity="quantityInBasket"
                    />
                    <div
                        v-else
                        class="flex items-center gap-2 w-32"
                    >
                        <span class="text-sm">Quantity</span>
                        <input
                            v-model="quantity"
                            placeholder="1"
                            class="bg-grey-lighter rounded border-grey-lighter transition-fast w-full px-3 py-2 w-16 text-center"
                            type="number"
                        >
                    </div>

                    <div
                        v-if="totalPrice > 0"
                        class="mb-2 text-xl font-semibold"
                    >
                        £{{ totalPrice }}
                    </div>
                </div>

                <div class="text-xs text-grey-dark mt-5 mb-10">
                    <nuxt-link
                        class="inline-block"
                        to="/terms-and-conditions"
                    >
                        View our product customisation terms and conditions.
                    </nuxt-link>.
                </div>

                <div class="mt-5">
                    <product-stock-notification
                        v-if="showStockNotification"
                        ref="productStockNotification"
                        :product="product"
                    />
                </div>
            </div>

        </div>

    </div>
</template>

<script>
import ProductCustomisationCanvas from '@/components/ProductCustomisationCanvas';
import CloseIcon from '~/static/fonts/LineIcons/close.svg';
import BasketIcon from '~/static/fonts/LineIcons/shopping-basket.svg';
import ProductCustomisationVariationInput from '~/components/ProductCustomisationVariationInput';
import Button from '~/components/Button';
import ProductQuantityInput from '~/components/ProductQuantityInput';
import ProductStockNotification from '~/components/ProductStockNotification';
import handleException from '../plugins/error-handler.client';

export default {

    components: {
        CloseIcon,
        BasketIcon,
        ProductCustomisationVariationInput,
        Button,
        ProductQuantityInput,
        ProductCustomisationCanvas,
        ProductStockNotification,
    },

    props: {
        product: {
            type: Object,
            required: true,
        },
        showPreview: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            imageScale: 1,
            isBuyButtonLoading: false,
        };
    },

    computed: {
        // Vuex
        text: {
            get() {
                return this.$store.getters['product/text'];
            },
            set(value) {
                this.$store.dispatch('product/setText', value);
            },
        },
        textLine2: {
            get() {
                return this.$store.getters['product/textLine2'];
            },
            set(value) {
                this.$store.dispatch('product/setTextLine2', value);
            },
        },
        selectedVariations: {
            get() {
                return this.$store.getters['product/selectedVariations'];
            },
            set(value) {
                this.$store.dispatch('product/setSelectedVariations', value);
            },
        },
        quantity: {
            get() {
                return this.$store.getters['product/quantity'];
            },
            set(value) {
                this.$store.dispatch('product/setQuantity', value);
                this.updateTotalPrice();
            },
        },
        totalPrice: {
            get() {
                return this.$store.getters['product/totalPrice'];
            },
            set(value) {
                this.$store.dispatch('product/setTotalPrice', value);
            },
        },
        customerImageUrl: {
            get() {
                return this.$store.getters['product/customerImageUrl'];
            },
            set(value) {
                this.$store.dispatch('product/setCustomerImageUrl', value);
            },
        },
        customerImagePreview: {
            get() {
                return this.$store.getters['product/customerImagePreview'];
            },
            set(value) {
                this.$store.dispatch('product/setCustomerImagePreview', value);
            },
        },
        customerImageWidth: {
            get() {
                return this.$store.getters['product/customerImageWidth'];
            },
            set(value) {
                this.$store.dispatch('product/setCustomerImageWidth', value);
            },
        },
        customerImageHeight: {
            get() {
                return this.$store.getters['product/customerImageHeight'];
            },
            set(value) {
                this.$store.dispatch('product/setCustomerImageHeight', value);
            },
        },
        canvasPreview: {
            get() {
                return this.$store.getters['product/canvasPreview'];
            },
            set(value) {
                this.$store.dispatch('product/setCanvasPreview', value);
            },
        },
        image: {
            get() {
                return this.$store.getters['product/customisationImage'];
            },
            set(value) {
                this.$store.dispatch('product/setCustomisationImage', value);
            },
        },
        defaultImage: {
            get() {
                return this.$store.getters['product/defaultImage'];
            },
            set(value) {
                this.$store.dispatch('product/setDefaultImage', value);
            },
        },
        isVariationImage: {
            get() {
                return this.$store.getters['product/isVariationImage'];
            },
            set(value) {
                this.$store.dispatch('product/setIsVariationImage', value);
            },
        },
        showStockNotification: {
            get() {
                return this.$store.getters['product/showStockNotification'];
            },
            set(value) {
                this.$store.dispatch('product/setShowStockNotification', value);
            },
        },
        setIsBuyButtonLoading: {
            get() {
                return this.$store.getters['product/isBuyButtonLoading'];
            },
            set(value) {
                this.$store.dispatch('product/setIsBuyButtonLoading', value);
            },
        },
        fontFamily: {
            get() {
                return this.$store.getters['product/fontFamily'];
            },
            set(value) {
                this.$store.dispatch('product/setFontFamily', value);
            },
        },
        activeVariationImage: {
            get() {
                return this.$store.getters['product/activeVariationImage'];
            },
            set(value) {
                this.$store.dispatch('product/setActiveVariationImage', value);
            },
        },

        productVariationsCompound() {
            return [
                this.product.id,
                this.selectedVariationIds.join('-'),
            ].join('-');
        },
        selectedVariationIds() {
            return this.selectedVariations.map((variation) => variation.ids).flat().sort();
        },
        selectedVariationsAreInStock() {
            return Array.isArray(this.product.variation_types) && this.product
                .variation_types
                .filter((type) => type.variations.some((variation) => this.selectedVariationIds.includes(variation.id)))
                .every((type) => type.variations.filter((variation) => this.selectedVariationIds.includes(variation.id))
                    .every((variation) => variation.is_in_stock));
        },
        requiredVariationTypes() {
            return Object.values(this.product.variation_types).filter((type) => type.required);
        },
        canBuy() {
            return this.selectedVariationsAreInStock && this.requiredVariationTypes.every((element) => this.selectedVariations.some((selected) => selected.variationType === element.id));
        },
        quantityInBasket() {
            const basketItems = this.$store.getters['basket/items'];
            const currentProduct = basketItems.find((item) => item.productVariationsCompound === this.productVariationsCompound && item.textVariation === this.text);
            return currentProduct ? currentProduct.quantity : 0;
        },
        backgroundImageUrl() {
            return this.image;
        },
        textVariations() {
            return Object.values(this.product.variation_types).filter((type) => type.text);
        },
        textVariation() {
            return this.hasTextVariation && this.textVariations[0].variations[0];
        },
        hasTextVariation() {
            return !!this.textVariations.length;
        },
        fontSize() {
            return `${this.textVariation.text_size}px`;
        },
        top() {
            return `${this.textVariation.text_y_offset}%`;
        },
        left() {
            return `${this.textVariation.text_x_offset}%`;
        },
        width() {
            return this.textVariation.text_width ? `${this.textVariation.text_width}px` : null;
        },
        color() {
            return this.textVariation.text_colour;
        },
        letterSpacing() {
            return `${this.textVariation.text_letter_spacing}px`;
        },
        lineHeight() {
            return `${this.textVariation.text_line_height}%`;
        },
        productPrice() {
            if (this.product.price > 0 && this.product.offer_price !== null) {
                return this.product.offer_price;
            }

            return this.product.price;
        },
    },

    mounted() {
        this.fontFamily = this.product.text_font_family ?? 'Mellony Dry Brush';

        this.loadFonts();
    },

    methods: {
        displayStockNotification() {
            this.showStockNotification = true;
            setTimeout(() => {
                this.$refs.productStockNotification.$el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }, 100);
        },
        imageUpdated(event) {
            this.image = this.getImage();
            this.isVariationImage = false;
            this.customerImageUrl = event.url;
            this.customerImagePreview = event.preview;
            this.customerImageHeight = event.height;
            this.customerImageWidth = event.width;
        },
        getImage() {
            if (this.hasTextVariation && this.textVariation.text_image.sizes.large) {
                return this.textVariation.text_image.sizes.large;
            }

            if (this.product.background_image?.sizes?.large) {
                return this.product.background_image.sizes.large;
            }

            return this.product.images[0]?.sizes?.large;
        },
        handleModalBeforeOpen({ params }) {
            this.product = params.product;
            this.selectedVariations = [];
            this.text = '';
            this.image = this.getImage();
            this.defaultImage = this.image;
            this.totalPrice = parseFloat(this.productPrice).toFixed(2);
            this.fontFamily = this.product.text_font_family ?? 'Mellony Dry Brush';
        },
        closeModal() {
            this.$modal.hide('product-customisation-modal');
        },
        updateSelectedVariations({ variationType, variationIds, text, textLine2, price, newSelectionId, deselect }) {
            // if this is a bunch size selection, update the image
            const selectedVariation = variationType.variations.find((variation) => variation.id === newSelectionId);
            const variationImage = selectedVariation ? selectedVariation.image.sizes.large : null;

            if (variationImage && !deselect && variationType.layer_position === 0) {
                this.isVariationImage = true;
                this.activeVariationImage = variationImage;
            } else {
                this.isVariationImage = false;
                this.image = this.getImage();
                this.activeVariationImage = null;

                if (text || variationIds.length > 0) {
                    this.$store.dispatch('product/setShowProductCustomisationImage', true);
                }
            }

            this.text = text === null ? this.text : text;
            this.textLine2 = textLine2 === null ? this.textLine2 : textLine2;

            this.removeSelection(variationType.id);

            if (variationIds.length > 0) {
                const selection = {
                    variationType: variationType.id,
                    ids: variationIds,
                    text,
                    price,
                };

                this.$store.dispatch('product/addSelectedVariation', selection);
            }

            // update total price
            this.updateTotalPrice();

            // TODO remove the text variation if the text is an empty string
            // if (this.selectedVariations.Text && this.selectedVariations.Text.text === '') {
            //     this.$delete(this.selectedVariations, 'Text');
            // }
        },
        updateTotalPrice() {
            const price = this.selectedVariations.reduce((acc, variation) => acc + parseFloat(variation.price), parseFloat(this.productPrice));

            this.totalPrice = (price * (this.quantity || 1)).toFixed(2);
        },
        removeSelection(variationTypeId) {
            this.$store.dispatch('product/removeSelectedVariation', variationTypeId);
        },
        handleBuyButtonClick() {
            this.isBuyButtonLoading = true;
            this.$store.dispatch('basket/updateItem', {
                productId: this.product.id,
                productVariationsCompound: this.productVariationsCompound,
                productTextVariation: this.text,
                productTextVariationLine2: this.textLine2,
                customerImageUrl: this.customerImageUrl,
                canvasPreview: this.canvasPreview,
                quantity: this.quantity || 1,
                fontId: this.$store.getters['product/fontId'],
            })
                .then(() => {
                    this.isBuyButtonLoading = false;
                    this.closeModal();
                    this.$modal.show('product-added-to-basket-modal', {
                        product: this.product,
                    });
                }).catch((error) => {
                    this.isBuyButtonLoading = false;
                    handleException(error);
                });
        },
        loadFonts() {
            if (typeof window !== 'undefined' && this.product.fonts.length > 0) {
                try {
                    const WebFont = require('webfontloader');

                    WebFont.load({
                        custom: {
                            families: this.product.fonts.map((font) => font.name),
                            urls: this.product.fonts.map((font) => font.url),
                        },
                    });
                } catch (e) {
                    console.error('WebFontLoader not found');
                }
            }
        },
        updateCanvasPreview(event) {
            this.canvasPreview = event;
        },
    },

};
</script>

<style lang="scss" scoped>
.close-icon {
    @apply h-5 cursor-pointer transition-fast;
    &:hover {
        @apply opacity-50;
    }
}

.image-wrapper {
    margin: 0 auto;
    height: 200px;
    width: 200px;
    @screen sm {
        height: 300px;
        width: 300px;
    }
    @screen lg {
        height: auto;
        width: auto;
    }
    &__bg {
        @apply absolute;
        background-position: center;
        background-size: cover;
        filter: blur(50px);
        top: -50px;
        right: -50px;
        bottom: -50px;
        left: -50px;
    }
}

.image {
    @apply relative;
    background-size: cover;
    background-repeat: no-repeat;
    height: 500px;
    width: 500px;
    transform-origin: top left;
}

.text {
    @apply absolute font-heading text-2xl text-center;
    transform: translate(-50%, -50%);
    padding: 20px;
}
</style>
